import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { Loader2, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#E11D48", "#0EA5E9", "#7C3AED", "#14B8A6"];

export default function AdminReports() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-revenue"], queryFn: () => api.get("/admin/revenue-report").then((r) => r.data), staleTime: 0, refetchOnMount: "always" });
  if (isLoading || !data)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-reports-page">
      <div>
        <h1 className="text-3xl font-bold">Gelir Raporu</h1>
        <p className="text-muted-foreground mt-1">Aylık gelir ve abonelik dağılımı</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">Toplam Gelir</h2>
        </div>
        <p className="font-display text-4xl font-bold" data-testid="report-total-revenue">{money(data.total_revenue)}</p>
        <p className="text-xs text-muted-foreground mt-1">Başarılı PayTR ödemeleri (abonelik + ek paket)</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="font-semibold mb-4">Son 6 Ay — Aylık Gelir (₺)</h2>
        <div style={{ width: "100%", height: 280 }} data-testid="revenue-chart">
          <ResponsiveContainer>
            <BarChart data={data.monthly} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money(v)} cursor={{ fill: "rgba(79,70,229,0.06)" }} />
              <Bar dataKey="revenue" fill="#4F46E5" radius={[6, 6, 0, 0]} maxBarSize={54} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-semibold mb-4">Plan Dağılımı</h2>
          {data.plan_distribution.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Veri yok</p>
          ) : (
            <div style={{ width: "100%", height: 260 }} data-testid="plan-pie">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.plan_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={90} label={(e) => `${e.name} (${e.value})`} labelLine={false} fontSize={11}>
                    {data.plan_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-semibold mb-4">Abonelik Durumu</h2>
          <div style={{ width: "100%", height: 260 }} data-testid="state-pie">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.state_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={55} outerRadius={90} label={(e) => `${e.name} (${e.value})`} labelLine={false} fontSize={11}>
                  {data.state_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
